import type {
  ArtworkOption,
  ArtworkOverlayGroup,
  OverlaySelectionsByGroup,
  SelectableArtworkItem
} from './digitalProofingArtworkTypes'

export const NO_SELECTION_ID = '-1'

export const selectionId = (item?: SelectableArtworkItem | null) =>
  item?.artwork_overlay_id ?? item?.artwork_id ?? item?.id

export const normalizedSelectionId = (item?: SelectableArtworkItem | null) => {
  const id = selectionId(item)
  return id === undefined || id === null ? NO_SELECTION_ID : String(id)
}

export const findSelection = <T extends SelectableArtworkItem>(items: T[], selectedId: string | number | null) =>
  items.find(item => normalizedSelectionId(item) === String(selectedId))

export const sortPosition = (item: SelectableArtworkItem) =>
  item.sort_order ?? item.order_artwork ?? item.order_coupon ?? 0

export const sortByPosition = <T extends SelectableArtworkItem>(items: T[] = []) =>
  [...items].sort((a, b) => sortPosition(a) - sortPosition(b))

export const overlayGroupId = (group: ArtworkOverlayGroup) => String(group.id)

export const groupOptions = (group: ArtworkOverlayGroup) => sortByPosition(group.artwork_overlays ?? [])

export const visibleOverlayGroupsForArtwork = (artwork?: ArtworkOption | false | null) =>
  (artwork && artwork.artwork_overlay_groups ? artwork.artwork_overlay_groups : [])
    .map(group => ({
      ...group,
      artwork_overlays: groupOptions(group)
    }))
    .filter(group => group.artwork_overlays.length > 0)

export const isRequiredSingleOptionGroup = (group: ArtworkOverlayGroup) =>
  Number(group.min_selections) === 1 && Number(group.max_selections) === 1

export const buildInitialOverlaySelections = (groups: ArtworkOverlayGroup[]): OverlaySelectionsByGroup =>
  groups.reduce<OverlaySelectionsByGroup>((selections, group) => {
    const options = groupOptions(group)
    selections[overlayGroupId(group)] =
      isRequiredSingleOptionGroup(group) && options.length === 1 ? [normalizedSelectionId(options[0])] : []

    return selections
  }, {})

export const sanitizeOverlaySelections = (
  groups: ArtworkOverlayGroup[],
  currentSelections: OverlaySelectionsByGroup
): OverlaySelectionsByGroup =>
  groups.reduce<OverlaySelectionsByGroup>((selections, group) => {
    const options = groupOptions(group)
    const optionIds = new Set(options.map(normalizedSelectionId))
    const maximumSelections = Number(group.max_selections)
    let selectedIds = (currentSelections[overlayGroupId(group)] ?? []).filter(id => optionIds.has(id))

    if (maximumSelections > 0 && selectedIds.length > maximumSelections) {
      selectedIds = selectedIds.slice(0, maximumSelections)
    }

    // Auto-select only when the shopper has exactly one valid required option available.
    selections[overlayGroupId(group)] =
      selectedIds.length === 0 && isRequiredSingleOptionGroup(group) && options.length === 1
        ? [normalizedSelectionId(options[0])]
        : selectedIds

    return selections
  }, {})

export const overlaySelectionsEqual = (left: OverlaySelectionsByGroup, right: OverlaySelectionsByGroup) => {
  const groupIds = new Set([...Object.keys(left), ...Object.keys(right)])
  return [...groupIds].every(groupId => {
    const leftIds = left[groupId] ?? []
    const rightIds = right[groupId] ?? []
    return leftIds.length === rightIds.length && leftIds.every((id, index) => id === rightIds[index])
  })
}

export const overlayGroupSelectionValid = (group: ArtworkOverlayGroup, selectionsByGroup: OverlaySelectionsByGroup) => {
  const selectedCount = (selectionsByGroup[overlayGroupId(group)] ?? []).length
  return selectedCount >= Number(group.min_selections) && selectedCount <= Number(group.max_selections)
}

export const cloneFormData = (formData: FormData) => {
  const copy = new FormData()
  for (const [key, value] of formData.entries()) {
    copy.append(key, value)
  }
  return copy
}
